import React from 'react';
import { Head, Link, useForm } from '@inertiajs/react';
import AdminLayout from '../../../../components/AdminLayout';
import { Button } from '../../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../components/ui/card';
import { Checkbox } from '../../../../components/ui/checkbox';
import { Badge } from '../../../../components/ui/badge';
import { ArrowLeft, Save } from 'lucide-react';

interface FilterValue {
  id: number;
  value: string;
  active: boolean;
  position: number;
  physiotherapists_count?: number;
}

interface FilterCategory {
  id: number;
  name: string;
  key: string;
  description: string | null;
  active: boolean;
  position: number;
  filter_values: FilterValue[];
}

interface PhysioFilterValue {
  id: number;
  value: string;
  filter_category: {
    id: number;
    name: string;
    key: string;
  };
}

interface Props {
  physiotherapist: {
    id: number;
    first_name: string;
    last_name: string;
  };
  filter_categories: FilterCategory[];
  physio_filter_values: PhysioFilterValue[];
}

const PhysiotherapistFilters: React.FC<Props> = ({ 
  physiotherapist, 
  filter_categories, 
  physio_filter_values 
}) => {
  const [selectedFilters, setSelectedFilters] = React.useState<number[]>(
    physio_filter_values.map(fv => fv.id)
  );

  const { post, processing, setData } = useForm({
    filter_value_ids: selectedFilters
  });

  // Synchroniser selectedFilters avec le formulaire Inertia
  React.useEffect(() => {
    setData('filter_value_ids', selectedFilters);
  }, [selectedFilters, setData]);

  console.log('Filtres initiaux:', physio_filter_values);
  console.log('IDs sélectionnés:', selectedFilters);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Envoi des filtres:', selectedFilters);
    console.log('URL:', `/admin/physiotherapists/${physiotherapist.id}/update_filters`);
    
    post(`/admin/physiotherapists/${physiotherapist.id}/update_filters`);
  };

  const handleFilterToggle = (filterValueId: number, checked: boolean) => {
    if (checked) {
      setSelectedFilters(prev => [...prev, filterValueId]);
    } else {
      setSelectedFilters(prev => prev.filter(id => id !== filterValueId));
    }
  };

  const isFilterSelected = (filterValueId: number) => {
    return selectedFilters.includes(filterValueId);
  };

  return (
    <AdminLayout title={`Filtres de ${physiotherapist.first_name} ${physiotherapist.last_name}`}>
      <Head title={`Filtres - ${physiotherapist.first_name} ${physiotherapist.last_name} - Admin`} />
      
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button variant="ghost" size="sm" onClick={() => window.history.back()}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Filtres de {physiotherapist.first_name} {physiotherapist.last_name}
              </h1>
              <p className="text-gray-600">Gestion des filtres du kinésithérapeute</p>
            </div>
          </div>
          <Link href={`/admin/physiotherapists/${physiotherapist.id}`}>
            <Button variant="outline">
              Voir le profil
            </Button>
          </Link>
        </div>

        {/* Formulaire de filtres */}
        <form onSubmit={handleSubmit}>
          <Card>
            <CardHeader>
              <CardTitle>Sélection des filtres</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {filter_categories.map((category) => (
                <div key={category.id} className="space-y-4">
                  <div className="border-b border-gray-200 pb-2">
                    <h3 className="text-lg font-medium text-gray-900">{category.name}</h3>
                    {category.description && (
                      <p className="text-sm text-gray-600 mt-1">{category.description}</p>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {category.filter_values.map((filterValue) => (
                      <div key={filterValue.id} className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                        <Checkbox
                          id={`filter-${filterValue.id}`}
                          checked={isFilterSelected(filterValue.id)}
                          onCheckedChange={(checked) => handleFilterToggle(filterValue.id, checked as boolean)}
                        />
                        <div className="flex-1">
                          <label 
                            htmlFor={`filter-${filterValue.id}`}
                            className="text-sm font-medium text-gray-900 cursor-pointer"
                          >
                            {filterValue.value}
                          </label>
                          {filterValue.physiotherapists_count !== undefined && (
                            <p className="text-xs text-gray-500">
                              {filterValue.physiotherapists_count} kinésithérapeute(s)
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {/* Résumé des sélections */}
              <div className="border-t border-gray-200 pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {selectedFilters.length} filtre(s) sélectionné(s)
                    </p>
                    <p className="text-xs text-gray-500">
                      Cliquez sur "Enregistrer" pour appliquer les modifications
                    </p>
                  </div>
                  <Button type="submit" disabled={processing}>
                    <Save className="w-4 h-4 mr-2" />
                    {processing ? 'Enregistrement...' : 'Enregistrer les filtres'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </form>

        {/* Actions supplémentaires */}
        <Card>
          <CardHeader>
            <CardTitle>Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center space-x-4">
              <Link href={`/admin/physiotherapists/${physiotherapist.id}/edit`}>
                <Button variant="outline">
                  Modifier le kinésithérapeute
                </Button>
              </Link>
              <Link href="/admin/filter_categories">
                <Button variant="outline">
                  Gérer les catégories de filtres
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default PhysiotherapistFilters; 
